import { cn } from '../../styles/lib/utils';
import { GridCellText } from '../GridCellText';
import { LayerKindIcon } from './assets/layerKindIcons';
import { LayerInfoPopover } from './LayerInfoPopover';
import type { LayerInfo, LayerKind } from './types';

export type LayerNameCellProps = {
  name: string;
  layerKind?: LayerKind;
  /** Icon color override (derived from the layer's tile-config paint). */
  layerKindColor?: string;
  /** Icon outline override for area/circle kinds. Falls back to `layerKindColor`. */
  layerKindOutlineColor?: string;
  /** When provided, renders the info button + popover with this layer detail. */
  info?: LayerInfo;
  /** Controlled open state for the info popover (so the row can highlight in sync). */
  infoOpen?: boolean;
  onInfoOpenChange?: (open: boolean) => void;
  /** Side the info popover opens on. Defaults to `right` (the LayerInfoPopover default). */
  infoSide?: 'top' | 'right' | 'bottom' | 'left';
  /** Info-button test id, threaded to the popover trigger. Row callers suffix it with the row id. */
  infoTestId?: string;
  className?: string;
  'data-testid'?: string;
};

/** Name-column cell: layer-type icon + name, with an optional info popover button on the right. */
function LayerNameCell({
  name,
  layerKind,
  layerKindColor,
  layerKindOutlineColor,
  info,
  infoOpen,
  onInfoOpenChange,
  infoSide,
  infoTestId,
  className,
  'data-testid': dataTestId,
}: LayerNameCellProps) {
  return (
    <div
      className={`relative flex min-w-0 flex-1 items-center gap-2 ${className ?? ''}`}
      data-testid={dataTestId ?? 'layer-name-cell'}
    >
      <LayerKindIcon
        kind={layerKind}
        color={layerKindColor}
        outlineColor={layerKindOutlineColor}
        size={24}
        className="shrink-0"
      />
      <div
        className={cn(
          'min-w-0 flex-1',
          info && 'group-data-[hovered=true]/row:truncate group-data-[hovered=true]/row:pr-7',
          infoOpen && 'truncate pr-7',
        )}
      >
        <GridCellText>{name}</GridCellText>
      </div>
      {info && (
        // Out of flow so it takes no space at rest — the wrapper (not the button) owns
        // positioning because the popover's tooltip renders its own trigger span.
        <div className="absolute inset-y-0 right-0 flex items-center">
          <LayerInfoPopover
            layer={info}
            open={infoOpen}
            onOpenChange={onInfoOpenChange}
            side={infoSide}
            data-testid={infoTestId}
          />
        </div>
      )}
    </div>
  );
}

export { LayerNameCell };
